import { useEffect, useRef, useState } from "react";
import { monaWorks } from "@/mocks/portfolio";
import TiltCard from "@/components/base/TiltCard";
import FadeIn from "@/components/base/FadeIn";

export default function ManyMona() {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [current, setCurrent] = useState(0);
  const isDown = useRef(false);
  const startX = useRef(0);
  const startScroll = useRef(0);
  const dragged = useRef(false);

  const scrollToIndex = (i: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const child = el.children[i] as HTMLElement | undefined;
    if (!child) return;
    const left = child.offsetLeft - (el.clientWidth - child.clientWidth) / 2;
    el.scrollTo({ left, behavior: "smooth" });
  };

  const next = () => {
    const i = Math.min(current + 1, monaWorks.length - 1);
    scrollToIndex(i);
  };
  const prev = () => {
    const i = Math.max(current - 1, 0);
    scrollToIndex(i);
  };

  // Update current index based on scroll position
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const center = el.scrollLeft + el.clientWidth / 2;
      let closest = 0;
      let min = Infinity;
      for (let i = 0; i < el.children.length; i++) {
        const child = el.children[i] as HTMLElement;
        const childCenter = child.offsetLeft + child.clientWidth / 2;
        const d = Math.abs(childCenter - center);
        if (d < min) {
          min = d;
          closest = i;
        }
      }
      setCurrent(closest);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // Mouse drag support
  const onMouseDown = (e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    isDown.current = true;
    dragged.current = false;
    startX.current = e.pageX - el.offsetLeft;
    startScroll.current = el.scrollLeft;
  };
  const onMouseMove = (e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el || !isDown.current) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = x - startX.current;
    if (Math.abs(walk) > 5) dragged.current = true;
    el.scrollLeft = startScroll.current - walk;
  };
  const onMouseUp = () => {
    isDown.current = false;
  };

  return (
    <section id="many-mona" className="bg-background-50 py-24 md:py-36 overflow-hidden">
      <div className="px-6 md:px-12">
        {/* Heading */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <p className="font-display text-xs uppercase tracking-[0.25em] text-foreground-700 mb-4 title-eyebrow">
              ( 02 ) — Project Series
            </p>
            <h1 className="font-display font-black text-foreground-950 leading-[0.85] tracking-[-0.04em] text-[14vw] md:text-[10vw] lg:text-[9vw] whitespace-nowrap">
              <span className="title-caps">MANY.</span>
              <em className="font-serif italic font-light title-italic">Mona</em>
            </h1>
          </div>
          <div className="max-w-md">
            <p className="text-base md:text-lg text-foreground-700 leading-relaxed font-serif">
              The most-copied face in art history, multiplied again and again — until the original begins to dissolve into static.
            </p>
          </div>
        </div>

        {/* Tag chips */}
        <div className="flex flex-wrap items-center gap-3 mb-12">
          {["Photo Manipulation", "Gen AI", "2035"].map((tag, i) => (
            <span
              key={i}
              className="px-4 py-2 rounded-full border border-foreground-950/30 text-foreground-950 text-xs uppercase tracking-[0.18em] font-display"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Big slider */}
      <div className="relative" data-product-shop>
        <div
          ref={scrollRef}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          className="flex gap-4 md:gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory no-scrollbar px-[15%] py-2 select-none cursor-grab active:cursor-grabbing"
          style={{ scrollSnapType: "x mandatory" }}
        >
          {monaWorks.map((w, i) => (
            <FadeIn
              key={i}
              delay={i * 80}
              className={`flex-shrink-0 w-[70%] snap-center transition-opacity duration-700 ${
                i === current ? "opacity-100" : "opacity-50"
              }`}
            >
              <TiltCard className="aspect-[4/3] bg-background-100 overflow-hidden mb-4" maxTilt={6} scale={1.015}>
                <img
                  src={w.image}
                  alt={w.id}
                  draggable={false}
                  className="w-full h-full object-cover object-top"
                />
                {/* Roman-style painting frame */}
                <div className="absolute inset-0 z-10 pointer-events-none">
                  <div className="absolute inset-0 border-[4px] border-accent-500/50" />
                  <div className="absolute inset-[4px] border-[2px] border-accent-500/25" />
                  <div className="absolute top-[4px] left-[4px] w-5 h-5 border-t-[3px] border-l-[3px] border-accent-500" />
                  <div className="absolute top-[4px] right-[4px] w-5 h-5 border-t-[3px] border-r-[3px] border-accent-500" />
                  <div className="absolute bottom-[4px] left-[4px] w-5 h-5 border-b-[3px] border-l-[3px] border-accent-500" />
                  <div className="absolute bottom-[4px] right-[4px] w-5 h-5 border-b-[3px] border-r-[3px] border-accent-500" />
                </div>
              </TiltCard>
              <div className="flex items-center justify-between">
                <span className="font-display text-xs uppercase tracking-[0.2em] text-foreground-950">
                  {w.id}
                </span>
                <span className="font-display text-xs uppercase tracking-[0.2em] text-foreground-700">
                  {String(i + 1).padStart(2, "0")} / {String(monaWorks.length).padStart(2, "0")}
                </span>
              </div>
            </FadeIn>
          ))}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between gap-3 px-6 md:px-12 mt-10">
          <span className="font-display text-xs uppercase tracking-[0.25em] text-foreground-700">
            Drag or use arrows to explore
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={prev}
              disabled={current === 0}
              className="w-12 h-12 rounded-full border border-foreground-950/40 flex items-center justify-center text-foreground-950 hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-foreground-950"
              aria-label="Previous"
            >
              <i className="ri-arrow-left-line text-lg"></i>
            </button>
            <button
              onClick={next}
              disabled={current === monaWorks.length - 1}
              className="w-12 h-12 rounded-full border border-foreground-950/40 flex items-center justify-center text-foreground-950 hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-foreground-950"
              aria-label="Next"
            >
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}